import { useCallback, useState } from 'react';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import { downloadFilePost } from 'utils/DownloadRequest';
import Request from 'utils/Request';
import { EXPORT_ENTITY } from 'utils/StaticApiEndpoints';

/**
 * useExport - drives the shared export dialog (RAT-1865): loads the
 * exportable field list for an entity, tracks format/scope/field selection,
 * and triggers the CSV/Excel download. Any section wires this up by passing
 * its own `entity` key and current filter params — no export logic is
 * duplicated per section.
 *
 * @param {Object} config
 * @param {string} config.entity - Registered export entity key (e.g. 'merchant')
 * @param {Object} [config.currentFilters] - The section's active filter params,
 *   sent to the backend when scope is 'filtered'
 * @param {number} [config.filteredTotal] - Known row count for the current
 *   filters (e.g. from the list page's pagination total), shown in the footer
 */
export default function useExport({
  entity,
  currentFilters = {},
  filteredTotal,
} = {}) {
  const [fields, setFields] = useState([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [format, setFormat] = useState('csv');
  const [scope, setScope] = useState('filtered');
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const loadFields = useCallback(async () => {
    setIsLoadingFields(true);
    try {
      const response = await Request.get(`${EXPORT_ENTITY}/${entity}/fields`);
      const fieldList = response?.data || [];
      setFields(fieldList);
      setSelectedFields(new Set(fieldList.map((field) => field.key)));
    } catch (error) {
      showErrorAlert(error?.message || 'Failed to load export fields');
    } finally {
      setIsLoadingFields(false);
    }
  }, [entity]);

  const toggleField = useCallback((key) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const applyPreset = useCallback(
    (preset, matchTableKeys = []) => {
      if (preset === 'all') {
        setSelectedFields(new Set(fields.map((field) => field.key)));
      } else if (preset === 'none') {
        setSelectedFields(new Set());
      } else if (preset === 'match') {
        setSelectedFields(new Set(matchTableKeys));
      }
    },
    [fields],
  );

  const runExport = useCallback(async () => {
    if (!selectedFields.size) {
      showErrorAlert('Select at least one field to export');
      return;
    }

    setIsExporting(true);
    try {
      await downloadFilePost(
        `${EXPORT_ENTITY}/${entity}`,
        {
          format,
          scope,
          fields: Array.from(selectedFields),
          filters: scope === 'filtered' ? currentFilters : {},
        },
        `${entity}-export`,
        format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
        `.${format}`,
      );
      showSuccessAlert('Export downloaded');
    } catch (error) {
      showErrorAlert(error?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [entity, format, scope, selectedFields, currentFilters]);

  const rowCountLabel =
    scope === 'filtered' && typeof filteredTotal === 'number'
      ? filteredTotal.toLocaleString()
      : 'All records';

  return {
    fields,
    isLoadingFields,
    loadFields,
    format,
    setFormat,
    scope,
    setScope,
    selectedFields,
    toggleField,
    applyPreset,
    runExport,
    isExporting,
    rowCountLabel,
  };
}
